import * as yup from 'yup';
import { Info } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useRef, useState } from 'react';

import {
  AddProductMutation as AddProduct,
  AddProductInput,
  GetProductsQuery as GetProducts,
  ProductType,
} from '__generated__/graphql';

import { assetUrl, getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddProductMutation from './AddProductMutation';
import UI from '../../../../common/UI';
import useCurrencyInput from '../../../../common/useCurrencyInput';
import useMutation from '../../../../api/useMutation';
import useValidation from '../../../../common/useValidation';

type Event = GetProducts['project']['event'];
type AddedProduct = AddProduct['addProduct'];

const addProductSchema = yup.object().shape({
  info: yup.object().shape({
    title: yup.string().required(),
  }),
});

export interface AddProductFormProps {
  event: Event;
  standAlone?: boolean;
  onSuccess?: (product: AddedProduct) => void;
}

const AddProductForm = ({ event, standAlone = false, onSuccess }: AddProductFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const currencyInput = useCurrencyInput();

  const form = useForm<AddProductInput>({
    values: {
      event: {
        id: event.id,
      },
      type: null,
      stand_alone: standAlone,
      info: {
        title: '',
      },
      pricing: {
        promotions: [{}],
        hide_on_invoice: false,
      },
    },
  });

  const [addProduct, { error, loading }] = useMutation(
    AddProductMutation,
    {
      variables: {
        input: {
          ...form.values,
          restrictions: {
            ...form.values.restrictions,
            max_per_ticket: !form.values.stand_alone ? 1 : null,
            max_per_order: form.values.stand_alone ? 1 : null,
          },
        },
      },
      onCompleted: ({ addProduct }) => {
        notifier.success(t('notifications.product_added'));
        onSuccess?.(addProduct);
      },
      onError: () => {
      //
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: addProductSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const promotionKey = (prop: string) => `pricing.promotions[0].${prop}`;

  const amountRef = useRef<HTMLInputElement>();

  const [productType, setProductType] = useState<ProductType>();

  const titlePlaceholder = productType === ProductType.donation
    ? t('donation_title_placeholder')
    : (productType === ProductType.fee
      ? t('fee_title_placeholder')
      : t('product_title_placeholder'));

  const setProduct = () => {
    setProductType(ProductType.item);

    form.set((values) => ({
      ...values,
      restrictions: values.restrictions,
      type: ProductType.item,
    }));
  };

  const setDonation = () => {
    setProductType(ProductType.donation);

    form.set((values) => ({
      ...values,
      pricing: {
        promotions: [{}],
        hide_on_invoice: false,
      },
      restrictions: values.restrictions,
      type: ProductType.donation,
    }));

    if (amountRef.current) {
      amountRef.current.value = '';
    }
  };

  const setTicketFee = () => {
    setProductType(ProductType.fee);

    form.set((values) => ({
      ...values,
      restrictions: values.restrictions,
      type: ProductType.fee,
    }));
  };

  return (
    <UI.Form onSubmit={() => addProduct()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_product')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_product_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.type, touched: form.getTouched('type') }}>
          <UI.InputLabel>
            {t('which_kind_of_product')}
          </UI.InputLabel>
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            <UI.Radio
              onChange={() => setProduct()}
              checked={productType === ProductType.item}
              sc={{ radioPositionV: 'center', box: true }}
            >
              <UI.FlexContainer sc={{ alignItems: 'center' }}>
                <UI.Badge sc={{ size: 42, mr: 2, brand: 'secondary.50' }}>
                  <img
                    src={assetUrl('images/product-type-product.svg')}
                    alt={t('product')}
                    width={22}
                  />
                </UI.Badge>
                <UI.Div>
                  <UI.Strong sc={{ block: true }}>
                    {t('product')}
                  </UI.Strong>
                  {t('normal_product_description')}
                </UI.Div>
              </UI.FlexContainer>
            </UI.Radio>
            <UI.Radio
              onChange={() => setDonation()}
              checked={productType === ProductType.donation}
              sc={{ radioPositionV: 'center', box: true }}
            >
              <UI.FlexContainer sc={{ alignItems: 'center' }}>
                <UI.Badge sc={{ size: 42, mr: 2, brand: 'secondary.50' }}>
                  <img
                    src={assetUrl('images/product-type-donation.svg')}
                    alt={t('donation')}
                    width={22}
                  />
                </UI.Badge>
                <UI.Div>
                  <UI.Strong sc={{ block: true }}>
                    {t('donation')}
                  </UI.Strong>
                  {t('donation_product_description')}
                </UI.Div>
              </UI.FlexContainer>
            </UI.Radio>
            <UI.Radio
              onChange={() => setTicketFee()}
              checked={productType === ProductType.fee}
              sc={{ radioPositionV: 'center', box: true }}
            >
              <UI.FlexContainer sc={{ alignItems: 'center' }}>
                <UI.Badge sc={{ size: 42, mr: 2, brand: 'secondary.50' }}>
                  <img
                    src={assetUrl('images/product-type-ticketfee.svg')}
                    alt={t('fee')}
                    width={22}
                  />
                </UI.Badge>
                <UI.Div>
                  <UI.Strong sc={{ block: true }}>
                    {t('fee')}
                  </UI.Strong>
                  {t('fee_product_description')}
                </UI.Div>
              </UI.FlexContainer>
            </UI.Radio>
          </UI.FormGrid>
        </UI.InputGroup>

        {productType && (
          <UI.FadeIn>
            <UI.FormGrid>
              <UI.InputGroup sc={{ valid: !errors.stand_alone, touched: form.getTouched('stand_alone') }}>
                <UI.InputLabel htmlFor="add_product_type">
                  {t('product_type')}
                </UI.InputLabel>
                <UI.FormGrid sc={{ gutter: 0.25 }}>
                  <UI.Select {...form.select('stand_alone', [false, true])} id="add_product_type">
                    <option value="0">
                      {t(productType === ProductType.fee ? 'apply_once_per_ticket' : 'ticket_upgrade')}
                    </option>
                    <option value="1">
                      {t(productType === ProductType.fee ? 'apply_once_per_order' : 'stand_alone_product')}
                    </option>
                  </UI.Select>
                </UI.FormGrid>
              </UI.InputGroup>

              {productType !== ProductType.fee && (
                <UI.FadeIn key={form.values.stand_alone ? 'stand_alone' : 'ticket_upgrade'}>
                  <UI.Info icon={<Info />}>
                    <Trans
                      i18nKey={form.values.stand_alone
                        ? 'stand_alone_product_description'
                        : 'ticket_upgrade_description'}
                    >
                      <UI.Strong />
                    </Trans>
                  </UI.Info>
                </UI.FadeIn>
              )}

              <UI.FormGrid sc={{ columns: ['1fr', productType === ProductType.donation ? '1fr' : '1fr 1fr'] }}>
                <UI.InputGroup sc={{ valid: !errors['info.title'], touched: form.getTouched('info.title') }}>
                  <UI.InputLabel htmlFor="title">
                    {t('product_title')}
                    {' '}
                    <UI.RequiredMark />
                  </UI.InputLabel>
                  <UI.DebouncedInput
                    {...form.text('info.title')}
                    id="title"
                    maxLength={50}
                    placeholder={titlePlaceholder}
                  />
                  <UI.ErrorMessages attribute={t('product_title')} errors={errors['info.title']} />
                </UI.InputGroup>
                {productType !== ProductType.donation && (
                  <UI.InputGroup
                    sc={{
                      valid: !errors[promotionKey('amount')],
                      touched: form.getTouched(promotionKey('amount')),
                    }}
                  >
                    <UI.InputLabel htmlFor="price">
                      {productType === ProductType.fee ? t('amount') : t('selling_price')}
                    </UI.InputLabel>
                    <UI.CurrencyInput
                      {...form.text(promotionKey('amount'), {
                        inputHandler: {
                          parse: currencyInput.parse,
                          format: currencyInput.value,
                        },
                      })}
                      id="price"
                      ref={amountRef}
                    />
                    <UI.ErrorMessages attribute={t('amount')} errors={errors[promotionKey('amount')]} />
                  </UI.InputGroup>
                )}
              </UI.FormGrid>
            </UI.FormGrid>
          </UI.FadeIn>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_product')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddProductForm;
